import { createSelector } from '@reduxjs/toolkit';

interface RootState {
    bankidAuthFlow: any; 
}

const selectBankIDState = (state: RootState) => state.bankidAuthFlow;

export const selectBankDetails = createSelector(
    [selectBankIDState],
    (bankidState) => bankidState.bankDetails
);

export const selectQRCodeData = createSelector(
    [selectBankIDState],
    (bankidState) => bankidState.qrCodeData
);

// UI state selectors
export const selectIsInitializing = createSelector(
    [selectBankIDState],
    (bankidState) => bankidState.isInitializing
);

export const selectIsAuthenticating = createSelector(
    [selectBankIDState],
    (bankidState) => bankidState.isAuthenticating
);

export const selectIsQRModalOpen = createSelector(
    [selectBankIDState],
    (bankidState) => bankidState.isQRModalOpen
);

export const selectQRState = createSelector(
    [selectBankIDState],
    (bankidState) => bankidState.qrState
);

// Auth state selectors
export const selectIsAuthSuccessful = createSelector(
    [selectBankIDState],
    (bankidState) => bankidState.isAuthSuccessful
);

export const selectUserData = createSelector(
    [selectBankIDState],
    (bankidState) => bankidState.userData
);

// Error selectors
export const selectError = createSelector(
    [selectBankIDState],
    (bankidState) => bankidState.error
);

export const selectMessage = createSelector(
    [selectBankIDState],
    (bankidState) => bankidState.message
);

// Session selectors
export const selectSessionTimeout = createSelector(
    [selectBankIDState],
    (bankidState) => bankidState.sessionTimeout
);

export const selectNeedsReload = createSelector(
    [selectBankIDState],
    (bankidState) => bankidState.needsReload
);

export const selectIsReady = createSelector(
    [selectBankDetails, selectIsInitializing],
    (bankDetails, isInitializing) => 
        !isInitializing && bankDetails?.id && bankDetails?.autoStartToken
);

export const selectCanUseSameDevice = createSelector(
    [selectIsReady],
    (isReady) => isReady
);

export const selectCanUseQRCode = createSelector(
    [selectIsReady],
    (isReady) => isReady
);

export const selectIsLoading = createSelector(
    [selectIsInitializing, selectIsAuthenticating, selectQRState],
    (isInitializing, isAuthenticating, qrState) => 
        isInitializing || isAuthenticating || qrState.isLoading
);

export const selectHasError = createSelector(
    [selectError],
    (error) => !!error
);

export const selectIsAuthenticationInProgress = createSelector(
    [selectQRCodeData, selectIsAuthSuccessful, selectSessionTimeout],
    (qrCodeData, isAuthSuccessful, sessionTimeout) => 
        !!qrCodeData?.id && !isAuthSuccessful && !sessionTimeout
);

export const selectToggleReactivate = createSelector(
    [selectBankIDState],
    (bankidState) => bankidState.toggleReactivate
);

export const selectInactiveAccount = createSelector(
    [selectBankIDState],
    (bankidState) => bankidState.inactiveAccount
);

export const selectSsnMismatch = createSelector(
    [selectBankIDState],
    (bankidState) => bankidState.ssnMismatch
);

export const selectSsnUpdatePending = createSelector(
    [selectBankIDState],
    (bankidState) => bankidState.ssnUpdatePending
);

export const selectSsnUpdateRejected = createSelector(
    [selectBankIDState],
    (bankidState) => bankidState.ssnUpdateRejected
);

export const selectSsnRejectionReason = createSelector(
    [selectBankIDState],
    (bankidState) => bankidState.ssnRejectionReason
);

export const selectNameMismatchType = createSelector(
    [selectBankIDState],
    (bankidState) => bankidState.nameMismatchType
);
